// src/pages/Availability.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import { Inbox as InboxIcon, CalendarPlus } from 'lucide-react'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import luxon2Plugin from '@fullcalendar/luxon2'
import { api, doctorMe, educationMe, techMe, propertyMe, financialMe, alliedMe, legalMe, migrationMe, ProviderProfession, enabledStacks, isStackEnabled } from '../api'
import '../styles.css'
import Spinner from '../components/Spinner'
import AddBookingSheet from '../components/AddBookingSheet'
import {
  DEFAULT_TIME_ZONE,
  detectTimeZone,
  formatCurrencyAmount,
  getCurrencyForCountry,
  listTimeZoneOptions,
  normalizeCountryCode,
} from '../countryRules'

type Block = { startISO: string; endISO: string }

// Appointment shape (same as Appointments page)
type Appt = {
  offerId?: string
  selectionId?: string
  specialty?: string
  chosenSlotISO: string
  feeCents?: number
  status?: string
  createdAt?: string
  patientName?: string
  specialistName?: string
  patient?: {
    name?: string
    email?: string
    phone?: string
    gender?: string
    dob?: string
    medicareOrIhi?: string
    address?: { street?: string; state?: string; postalCode?: string }
  }
  meta?: Record<string, any>
}

// Bookings we render as foreground events
type Booking = { startISO: string; endISO: string; title: string; appt?: Appt }
const normalizeAppointments = (input: any): Appt[] => {
  if (Array.isArray(input)) return input
  if (Array.isArray(input?.items)) return input.items
  if (Array.isArray(input?.appointments)) return input.appointments
  return []
}
const isCancelledAppt = (a: Appt) => String(a?.status || '').toUpperCase() === 'CANCELLED'

const CANON_TZ = DEFAULT_TIME_ZONE
const DEFAULT_SLOT_MIN = 30 // used if an appointment has only a start time
const CONTACT_SLOT = 'REQUEST_BOOKING'

// ---------- helpers ----------
function isValidHHMM(s?: string) { return !!s && /^\d{2}:\d{2}$/.test(s) }
function clamp(n:number, min:number, max:number) { return Math.max(min, Math.min(max, n)) }

function money(cents?: number, currencyCode = 'AUD') {
  if (cents == null) return '—'
  return formatCurrencyAmount(cents / 100, currencyCode)
}
function fmtDateTimeLocal(iso?: string, timeZone: string = detectTimeZone()) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-AU', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return new Date(iso).toLocaleString()
  }
}
function formatAppointmentTime(slotISO?: string, createdAt?: string, timeZone: string = detectTimeZone()) {
  if (slotISO === CONTACT_SLOT) {
    return `Request submitted: ${fmtDateTimeLocal(createdAt, timeZone)} (${timeZone})`
  }
  return `Appointment time: ${fmtDateTimeLocal(slotISO, timeZone)} (${timeZone})`
}
function appointmentTimeLabel(slotISO?: string) {
  return slotISO === CONTACT_SLOT ? 'Request submitted' : 'Appointment time'
}
function addMinutes(iso: string, minutes: number) {
  const d = new Date(iso)
  d.setMinutes(d.getMinutes() + minutes)
  return d.toISOString()
}
function toGCalTime(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return [
    d.getUTCFullYear(),
    pad(d.getUTCMonth() + 1),
    pad(d.getUTCDate()),
    'T',
    pad(d.getUTCHours()),
    pad(d.getUTCMinutes()),
    pad(d.getUTCSeconds()),
    'Z',
  ].join('')
}
function toDisplayISO(iso: string, displayTz: string) {
  return DateTime.fromISO(iso).setZone(displayTz).toISO();
}
function normalizeBlocks(input: any): Block[] {
  if (!Array.isArray(input)) return []
  return input
    .map((b: any) => ({
      startISO: b?.startISO ?? b?.start ?? b?.from ?? b?.begin ?? null,
      endISO:   b?.endISO   ?? b?.end   ?? b?.to   ?? b?.finish ?? null,
    }))
    .filter(b => typeof b.startISO === 'string' && typeof b.endISO === 'string')
}

function blockId(b: Block) {
  return `${b.startISO}|${b.endISO}`
}

function diffBlocks(prev: Block[], curr: Block[]) {
  const prevIds = new Set(prev.map(blockId))
  const currIds = new Set(curr.map(blockId))
  const added   = curr.filter(b => !prevIds.has(blockId(b)))
  const removed = prev.filter(b => !currIds.has(blockId(b)))
  return { added, removed }
}

// ---------- simple in-memory cache per fetched range ----------
type RangeKey = string; // `${startISO}|${endISO}`
function rangeKey(s: Date, e: Date) {
  return `${s.toISOString()}|${e.toISOString()}`
}
function overlaps(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) {
  return aEnd > bStart && bEnd > aStart
}

const getApptKey = (a: Appt) => a.selectionId || a.offerId || a.chosenSlotISO
const hasStackProfile = (profile?: any) => {
  if (!profile || profile.needsRegistration) return false
  const status = String(profile.status || profile.approvalStatus || '').trim().toUpperCase()
  return status === 'APPROVED'
}
const RAW_PROVIDER_STACKS: Array<{
  id: ProviderProfession
  label: string
  registerPath: string
  fetchProfile: () => Promise<any>
}> = [
  { id: 'doctor', label: 'Medical Doctors', registerPath: '/professionals/doctors', fetchProfile: doctorMe },
  { id: 'tech', label: 'Tech & Creative', registerPath: '/professionals/tech', fetchProfile: techMe },
  { id: 'property', label: 'Property, Design & Building Services', registerPath: '/professionals/property', fetchProfile: propertyMe },
  { id: 'financial', label: 'Financial Advisory', registerPath: '/professionals/financial', fetchProfile: financialMe },
  { id: 'allied', label: 'Allied Health', registerPath: '/professionals/allied', fetchProfile: alliedMe },
  { id: 'legal', label: 'Legal & Document Services', registerPath: '/professionals/legal', fetchProfile: legalMe },
  { id: 'migration', label: 'Migration & Visa', registerPath: '/professionals/migration', fetchProfile: migrationMe },
  { id: 'education', label: 'Education & Tutoring', registerPath: '/professionals/education', fetchProfile: educationMe },
]

const PROVIDER_STACKS = RAW_PROVIDER_STACKS.filter((stack) => isStackEnabled(stack.id))

export default function AvailabilityPage() {
  const location = useLocation()
  const nav = useNavigate()
  const calRef = useRef<any>(null)
  const [me, setMe] = useState<any>(null)
  const [stackProfiles, setStackProfiles] = useState<Record<string, any>>({})
  const [stackStatuses, setStackStatuses] = useState<Record<string, 'unknown' | 'approved' | 'unapproved'>>({})
  const fetchedProfiles = useRef<Set<ProviderProfession>>(new Set())
  const [profilesReady, setProfilesReady] = useState(false)
  const [activeStack, setActiveStack] = useState<ProviderProfession>('doctor')
  const [activeAppt, setActiveAppt] = useState<Appt | null>(null)

  const [serverBlocks, setServerBlocks] = useState<Block[]>([])
  const [bookings, setBookings] = useState<Booking[]>([]) // calendar overlay
  const [displayTz, setDisplayTz] = useState<string>(() => detectTimeZone())
  const [displayTzLoaded, setDisplayTzLoaded] = useState(false)
  const displayTzSavedRef = useRef<string | null>(null)
  const [needsProviderProfile, setNeedsProviderProfile] = useState(false)
  const [showOvernight, setShowOvernight] = useState(false)
  const [viewReady, setViewReady] = useState(false)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string|null>(null)
  const [saveState, setSaveState] = useState<'idle'|'saving'|'saved'|'error'>('idle')
  const [saveMsg, setSaveMsg] = useState<string>('')
  const [gcalStatus, setGcalStatus] = useState<any>(null)
  const [gcalCalendars, setGcalCalendars] = useState<any[]>([])
  const [gcalSelected, setGcalSelected] = useState<string[]>([])
  const [gcalWriteId, setGcalWriteId] = useState<string | null>(null)
  const [gcalBusy, setGcalBusy] = useState<Block[]>([])
  const [gcalError, setGcalError] = useState<string | null>(null)
  const [gcalNotice, setGcalNotice] = useState<string | null>(null)
  const [gcalLoading, setGcalLoading] = useState(false)
  const [gcalSaving, setGcalSaving] = useState(false)
  const [gcalManageOpen, setGcalManageOpen] = useState(false)
  const [calStatus, setCalStatus] = useState<any>(null)
  const [calEventTypes, setCalEventTypes] = useState<any[]>([])
  const [calSelected, setCalSelected] = useState<string[]>([])
  const [calBusy, setCalBusy] = useState<Block[]>([])
  const [calError, setCalError] = useState<string | null>(null)
  const [calNotice, setCalNotice] = useState<string | null>(null)
  const [calLoading, setCalLoading] = useState(false)
  const [calSaving, setCalSaving] = useState(false)
  const [calManageOpen, setCalManageOpen] = useState(false)
  const [cancelingBooking, setCancelingBooking] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [addBookingOpen, setAddBookingOpen] = useState(false)
  const gcalRangeRef = useRef<{ start: Date; end: Date; stack: ProviderProfession } | null>(null)
  const calRangeRef = useRef<{ start: Date; end: Date; stack: ProviderProfession } | null>(null)

  // modal state (create/edit)
  const [modal, setModal] = useState<null | {
    mode: 'create' | 'edit'
    startISO: string
    endISO: string
    originalId?: string // for edit
  }>(null)

  // form inside modal
  const [formDurMin, setFormDurMin] = useState(30)
  const [formRepeat, setFormRepeat] = useState<'none'|'daily'|'weekly'|'monthly'>('none')
  const [formCount, setFormCount] = useState(1)     // number of repeats (including first)
  const [formUntil, setFormUntil] = useState<string>('') // optional YYYY-MM-DD

  // fetch-range cache
  const fetched = useRef<Map<RangeKey, Block[]>>(new Map())
  const previousStackRef = useRef<ProviderProfession>(activeStack)
  // Match inbox behavior: show stacks unless explicitly unapproved; resolve approval lazily.
  const availableStacks = React.useMemo(
    () => PROVIDER_STACKS.filter((stack) => stackStatuses[stack.id] !== 'unapproved'),
    [stackStatuses]
  )
  const activeStackMeta = React.useMemo(
    () =>
      availableStacks.find(stack => stack.id === activeStack) ||
      availableStacks[0] ||
      PROVIDER_STACKS[0],
    [activeStack, availableStacks]
  )
  const activeStackCurrency = React.useMemo(() => {
    const profile = stackProfiles[activeStack] || {}
    const countryCode = normalizeCountryCode(
      profile?.address?.countryCode || profile?.address?.country || profile?.countryCode || profile?.country || 'AU'
    )
    return getCurrencyForCountry(countryCode)
  }, [stackProfiles, activeStack])

  const disabledStackAppointments = useRef<Set<ProviderProfession>>(new Set())

  // Write local changes back into the cached ranges we’ve already fetched.
// This ensures those ranges won’t "forget" edits when you navigate.
function writeThroughCache(additions: Block[] = [], removals: Block[] = []) {
  // Helper to test if a block lies within a cached range
  const idSetRemovals = new Set(removals.map(blockId))
  for (const [rk, arr] of fetched.current.entries()) {
    const [sIso, eIso] = rk.split('|')
    const rs = new Date(sIso), re = new Date(eIso)

    // Remove any blocks that were deleted/overwritten
    let next = arr.filter(b => !idSetRemovals.has(blockId(b)))

    // Add any new/edited blocks that overlap this cached range
    for (const add of additions) {
      const bs = new Date(add.startISO), be = new Date(add.endISO)
      if (overlaps(rs, re, bs, be)) next.push(add)
    }

    // de-dupe
    const map = new Map<string, Block>()
    for (const b of next) map.set(blockId(b), b)
    fetched.current.set(rk, Array.from(map.values()))
  }
}
  
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => { if (toastTimer.current) clearTimeout(toastTimer.current) }
  }, [])

  useEffect(() => {
    if (previousStackRef.current === activeStack) return
    fetched.current = new Map()
    setServerBlocks([])
    setBookings([])
    setActiveAppt(null)
    setCancelError(null)
    setCancelingBooking(false)
    setNeedsProviderProfile(false)
    setViewReady(false)
    setErr(null)
    setSaveState('idle')
    setSaveMsg('')
    previousStackRef.current = activeStack
    // fetch profile for newly selected stack if not yet fetched
    if (!fetchedProfiles.current.has(activeStack)) {
      setProfilesReady(false)
    }
  }, [activeStack])

  // initial load (lightweight: just /auth/me)
  useEffect(() => {
    (async () => {
      try {
        const m = await api.me()
        setMe(m)
      } catch (e:any) {
        setErr(e?.message || 'Failed to load profile')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // Lazy-fetch only the active stack profile
  useEffect(() => {
    let cancelled = false
    if (fetchedProfiles.current.has(activeStack)) return
    fetchedProfiles.current.add(activeStack)
    ;(async () => {
      try {
        const profile = await activeStackMeta.fetchProfile().catch(() => ({ needsRegistration: true }))
        if (cancelled) return
        const normalized = profile || { needsRegistration: true }
        const approved = hasStackProfile(normalized)
        setStackProfiles((prev) => ({ ...prev, [activeStack]: normalized }))
        setStackStatuses((prev) => ({ ...prev, [activeStack]: approved ? 'approved' : 'unapproved' }))
        setNeedsProviderProfile(!approved)
      } catch (err: any) {
        const msg = String(err?.message || '').toLowerCase()
        const isAuth = msg.includes('forbidden') || msg.includes('unauthorized') || msg.includes('403')
        if (!cancelled) {
          setStackStatuses((prev) => ({ ...prev, [activeStack]: isAuth ? 'unapproved' : prev[activeStack] || 'unknown' }))
          if (isAuth) setNeedsProviderProfile(true)
        }
      } finally {
        if (!cancelled) {
          setProfilesReady(true)
          setViewReady(true)
        }
      }
    })()
    return () => { cancelled = true }
  }, [activeStack, activeStackMeta])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (!params.has('tab')) return
    params.delete('tab')
    const nextSearch = params.toString()
    nav(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true }
    )
  }, [location.search, location.pathname, location.hash, nav])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('gcal') !== 'connected') return
    setGcalNotice('Google Calendar connected to EUCA Match.')
    params.delete('gcal')
    const nextSearch = params.toString()
    nav(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true }
    )
  }, [location.search, location.pathname, location.hash, nav])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('calendly') !== 'connected') return
    setCalNotice('Calendly connected to EUCA Match.')
    params.delete('calendly')
    const nextSearch = params.toString()
    nav(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true }
    )
  }, [location.search, location.pathname, location.hash, nav])

  useEffect(() => {
    if (!gcalNotice) return
    const t = window.setTimeout(() => setGcalNotice(null), 3500)
    return () => window.clearTimeout(t)
  }, [gcalNotice])

  useEffect(() => {
    if (!calNotice) return
    const t = window.setTimeout(() => setCalNotice(null), 3500)
    return () => window.clearTimeout(t)
  }, [calNotice])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const stackParam = params.get('stack') as ProviderProfession | null
    if (stackParam && stackStatuses[stackParam] !== 'unapproved') {
      setActiveStack(stackParam)
    }
  }, [location.search, stackStatuses])

  useEffect(() => {
    // If nothing is approved, surface the CTA and stop spinning.
    if (!availableStacks.length && Object.keys(stackStatuses).length) {
      setNeedsProviderProfile(true)
      setViewReady(true)
    }
  }, [availableStacks.length, stackStatuses])

  useEffect(() => {
    if (!availableStacks.length) return
    if (!availableStacks.some((s) => s.id === activeStack)) {
      setActiveStack(availableStacks[0].id)
    }
  }, [availableStacks, activeStack])

  // Kick off the first availability fetch even before FullCalendar mounts,
  // so we can determine whether the profile is ready (and stop the spinner).
  useEffect(() => {
    if (!profilesReady) return
    // If we previously marked the profile as incomplete but now have an approved profile, reset flags.
    if (needsProviderProfile && stackStatuses[activeStack] === 'approved') {
      setNeedsProviderProfile(false)
      setViewReady(false)
    }
  }, [needsProviderProfile, stackProfiles, activeStack, profilesReady, stackStatuses])

  useEffect(() => {
    if (viewReady || needsProviderProfile || !profilesReady) return
    if (stackStatuses[activeStack] !== 'approved') {
      setNeedsProviderProfile(true)
      setViewReady(true)
      return
    }
    const now = new Date()
    const start = new Date(now); start.setDate(start.getDate() - 3)
    const end = new Date(now);   end.setDate(end.getDate() + 10)
    loadRange(start, end, activeStack)
  }, [viewReady, needsProviderProfile, activeStack, stackProfiles])

  // If we learn the profile is missing from any fetch, allow the CTA to render.
  useEffect(() => {
    if (needsProviderProfile && profilesReady) setViewReady(true)
  }, [needsProviderProfile, profilesReady])

  useEffect(() => {
    let cancelled = false
    if (!profilesReady || needsProviderProfile || stackStatuses[activeStack] !== 'approved') {
      setGcalStatus(null)
      setGcalBusy([])
      return
    }
    setGcalLoading(true)
    setGcalError(null)
    api.googleCalendarStatus(activeStack)
      .then((res: any) => {
        if (cancelled) return
        setGcalStatus(res)
        setGcalSelected(Array.isArray(res?.selectedCalendarIds) ? res.selectedCalendarIds : [])
        setGcalWriteId(res?.writeCalendarId || null)
      })
      .catch((e: any) => {
        if (cancelled) return
        setGcalError(e?.message || 'Failed to load Google Calendar status')
        setGcalStatus({ connected: false })
      })
      .finally(() => {
        if (!cancelled) setGcalLoading(false)
      })
    return () => { cancelled = true }
  }, [activeStack, profilesReady, needsProviderProfile, stackStatuses])

  useEffect(() => {
    let cancelled = false
    if (!profilesReady || needsProviderProfile || stackStatuses[activeStack] !== 'approved') {
      setCalStatus(null)
      setCalBusy([])
      return
    }
    setCalLoading(true)
    setCalError(null)
    api.calendlyStatus(activeStack)
      .then((res: any) => {
        if (cancelled) return
        setCalStatus(res)
        setCalSelected(Array.isArray(res?.selectedEventTypeUris) ? res.selectedEventTypeUris : [])
      })
      .catch((e: any) => {
        if (cancelled) return
        setCalError(e?.message || 'Failed to load Calendly status')
        setCalStatus({ connected: false })
      })
      .finally(() => {
        if (!cancelled) setCalLoading(false)
      })
    return () => { cancelled = true }
  }, [activeStack, profilesReady, needsProviderProfile, stackStatuses])

  useEffect(() => {
    let cancelled = false
    if (!profilesReady || needsProviderProfile || stackStatuses[activeStack] !== 'approved') {
      setDisplayTzLoaded(false)
      return
    }
    api.calendarSettings(activeStack)
      .then((res: any) => {
        if (cancelled) return
        const tz = typeof res?.timeZone === 'string' && res.timeZone.trim()
          ? res.timeZone.trim()
          : detectTimeZone()
        setDisplayTz(tz)
        displayTzSavedRef.current = tz
        setDisplayTzLoaded(true)
      })
      .catch(() => {
        if (cancelled) return
        const tz = detectTimeZone()
        setDisplayTz(tz)
        displayTzSavedRef.current = tz
        setDisplayTzLoaded(true)
      })
    return () => { cancelled = true }
  }, [activeStack, profilesReady, needsProviderProfile, stackStatuses])

  useEffect(() => {
    let cancelled = false
    if (!displayTzLoaded || !profilesReady || needsProviderProfile || stackStatuses[activeStack] !== 'approved') return
    if (!displayTz || displayTz === displayTzSavedRef.current) return
    const nextTz = displayTz
    api.calendarSettingsUpdate(activeStack, { timeZone: nextTz })
      .then(() => {
        if (cancelled) return
        displayTzSavedRef.current = nextTz
      })
      .catch(() => {
        if (cancelled) return
      })
    return () => { cancelled = true }
  }, [displayTz, displayTzLoaded, activeStack, profilesReady, needsProviderProfile, stackStatuses])

  const isForbiddenError = (err: any) =>
    typeof err?.message === 'string' && err.message.toLowerCase().includes('forbidden')
  const isStackMisconfigured = (err: any) =>
    typeof err?.message === 'string' && err.message.includes('tableName')
  const gcalSelectedKey = useMemo(
    () => (Array.isArray(gcalStatus?.selectedCalendarIds) ? gcalStatus.selectedCalendarIds.join('|') : ''),
    [gcalStatus]
  )
  const gcalSelectedSet = useMemo(() => new Set(gcalSelected), [gcalSelected])
  const displayTzOptions = useMemo(() => {
    return listTimeZoneOptions(displayTz ? [displayTz] : [])
  }, [displayTz])
  const gcalWriteOptions = useMemo(
    () => gcalCalendars.filter((cal) => gcalSelectedSet.has(cal.id)),
    [gcalCalendars, gcalSelectedSet]
  )
  const calSelectedKey = useMemo(
    () => (Array.isArray(calStatus?.selectedEventTypeUris) ? calStatus.selectedEventTypeUris.join('|') : ''),
    [calStatus]
  )
  const calSelectedSet = useMemo(() => new Set(calSelected), [calSelected])

  // -------- data fetching ----------
  const loadLegacyAppointments = async (start: Date, end: Date) => {
    try {
      const appts = await api.getAppointments('specialist')
      const arr = normalizeAppointments(appts).filter(a => !isCancelledAppt(a))
      const mapped: Booking[] = arr
        .filter((a: any) => a?.chosenSlotISO || a?.startISO)
        .map((a: any) => {
          const startISO: string = a.chosenSlotISO ?? a.startISO
          const endISO: string =
            a.endISO
              ? a.endISO
              : DateTime.fromISO(startISO).plus({ minutes: DEFAULT_SLOT_MIN }).toUTC().toISO()!
          const title: string =
            a.patientName
              ? `Booked — ${a.patientName}`
              : a.title || 'Booked'
          return { startISO, endISO, title, appt: a as Appt }
        })
        .filter(b => {
          const bs = new Date(b.startISO)
          const be = new Date(b.endISO)
          return overlaps(start, end, bs, be)
        })
      setBookings(mapped)
    } catch (fallbackErr) {
      console.warn('fallback getAppointments failed', fallbackErr)
    }
  }

  async function loadGoogleBusy(start: Date, end: Date, stackId: ProviderProfession = activeStack) {
    if (!gcalStatus?.connected) {
      setGcalBusy([])
      return
    }
    const selected = Array.isArray(gcalStatus?.selectedCalendarIds) ? gcalStatus.selectedCalendarIds : []
    if (!selected.length) {
      setGcalBusy([])
      return
    }
    try {
      const res = await api.googleCalendarBusy(stackId, start.toISOString(), end.toISOString())
      const got = normalizeBlocks(res?.busy)
      setGcalBusy(got)
      setGcalError(null)
    } catch (e: any) {
      console.warn('google busy load failed', e)
      setGcalBusy([])
      setGcalError('Google Calendar sync error — reconnect if this continues.')
    }
  }

  async function loadCalendlyBusy(start: Date, end: Date, stackId: ProviderProfession = activeStack) {
    if (!calStatus?.connected) {
      setCalBusy([])
      return
    }
    try {
      const res = await api.calendlyBusy(stackId, start.toISOString(), end.toISOString())
      const got = normalizeBlocks(res?.busy)
      setCalBusy(got)
      setCalError(null)
    } catch (e: any) {
      console.warn('calendly busy load failed', e)
      setCalBusy([])
      setCalError('Calendly sync error — reconnect if this continues.')
    }
  }

  async function loadRange(start: Date, end: Date, stackId: ProviderProfession = activeStack) {
    if (needsProviderProfile || !profilesReady) return
    if (!hasStackProfile(stackProfiles[stackId])) {
      setNeedsProviderProfile(true)
      setViewReady(true)
      return
    }
    const key = rangeKey(start, end)
    if (!fetched.current.has(key)) {
      try {
        if (disabledStackAppointments.current.has(stackId)) {
          setServerBlocks(mergeBlocksFromCacheVisible(start, end))
          await loadLegacyAppointments(start, end)
          return
        }
        // Availability (free windows)
        const res = await api.getStackAvailabilityRange(stackId, start.toISOString(), end.toISOString())
        const got = normalizeBlocks(res?.blocks)
        fetched.current.set(key, got)
        setNeedsProviderProfile(false)
        setViewReady(true)
      } catch (e:any) {
        if (isStackMisconfigured(e)) {
          disabledStackAppointments.current.add(stackId)
          await loadLegacyAppointments(start, end)
        } else if (isForbiddenError(e)) {
          setNeedsProviderProfile(true)
          setViewReady(true)
          setErr(null)
          return
        } else {
          console.error('loadRange failed', e)
          setErr(e?.message || 'Failed to load availability')
        }
      }
    }
    setServerBlocks(mergeBlocksFromCacheVisible(start, end))
    gcalRangeRef.current = { start, end, stack: stackId }
    calRangeRef.current = { start, end, stack: stackId }

    // Bookings (confirmed appts) — always refresh for the visible range
    try {
      if (disabledStackAppointments.current.has(stackId)) {
        await loadLegacyAppointments(start, end)
        return
      }
      const appts = await api.getStackAppointments(stackId, 'specialist')
      const arr = normalizeAppointments(appts).filter(a => !isCancelledAppt(a))
      const mapped: Booking[] = arr
        .filter((a: any) => a?.chosenSlotISO || a?.startISO)
        .map((a: any) => {
          const startISO: string = a.chosenSlotISO ?? a.startISO
          const endISO: string =
            a.endISO
              ? a.endISO
              : DateTime.fromISO(startISO).plus({ minutes: DEFAULT_SLOT_MIN }).toUTC().toISO()!
          const title: string =
            a.patientName
              ? `Booked — ${a.patientName}`
              : a.title || 'Booked'
          return { startISO, endISO, title, appt: a as Appt }
        })
        .filter(b => {
          const bs = new Date(b.startISO)
          const be = new Date(b.endISO)
          return overlaps(start, end, bs, be)
        })
      setBookings(mapped)
    } catch (e:any) {
      if (isStackMisconfigured(e)) {
        disabledStackAppointments.current.add(stackId)
        await loadLegacyAppointments(start, end)
      } else if (isForbiddenError(e)) {
        setNeedsProviderProfile(true)
      } else {
        console.warn('getAppointments failed (continuing without bookings)', e)
      }
    }

    await loadGoogleBusy(start, end, stackId)
    await loadCalendlyBusy(start, end, stackId)
  }

  useEffect(() => {
    if (!gcalStatus?.connected || !gcalSelectedKey) {
      setGcalBusy([])
      return
    }
    let current = gcalRangeRef.current
    if (!current || current.stack !== activeStack) {
      const view = calRef.current?.getApi?.()?.view
      if (view?.activeStart && view?.activeEnd) {
        const start = new Date(view.activeStart); start.setDate(start.getDate() - 2)
        const end = new Date(view.activeEnd); end.setDate(end.getDate() + 2)
        current = { start, end, stack: activeStack }
        gcalRangeRef.current = current
      }
    }
    if (current && current.stack === activeStack) {
      loadGoogleBusy(current.start, current.end, activeStack)
    }
  }, [gcalStatus?.connected, gcalSelectedKey, activeStack])

  useEffect(() => {
    if (!calStatus?.connected) {
      setCalBusy([])
      return
    }
    let current = calRangeRef.current
    if (!current || current.stack !== activeStack) {
      const view = calRef.current?.getApi?.()?.view
      if (view?.activeStart && view?.activeEnd) {
        const start = new Date(view.activeStart); start.setDate(start.getDate() - 2)
        const end = new Date(view.activeEnd); end.setDate(end.getDate() + 2)
        current = { start, end, stack: activeStack }
        calRangeRef.current = current
      }
    }
    if (current && current.stack === activeStack) {
      loadCalendlyBusy(current.start, current.end, activeStack)
    }
  }, [calStatus?.connected, calSelectedKey, activeStack])

  function mergeBlocksFromCacheVisible(start: Date, end: Date) {
    const out: Block[] = []
    for (const [rk, arr] of fetched.current.entries()) {
      const [sIso, eIso] = rk.split('|')
      const rs = new Date(sIso), re = new Date(eIso)
      if (overlaps(start, end, rs, re)) out.push(...arr)
    }
    // de-dupe by pair
    const map = new Map<string, Block>()
    for (const b of out) map.set(`${b.startISO}|${b.endISO}`, b)
    return Array.from(map.values())
  }

  // Collect every block we've ever fetched/edited (all ranges)
function getAllBlocksFromCache(): Block[] {
  const out: Block[] = []
  for (const [, arr] of fetched.current.entries()) out.push(...arr)
  // de-dupe + stable order
  const map = new Map<string, Block>()
  for (const b of out) map.set(`${b.startISO}|${b.endISO}`, b)
  return Array.from(map.values()).sort((a,b)=> a.startISO.localeCompare(b.startISO))
}

// save helper — ALWAYS send union of (cache + latest state) so future weeks don't vanish
async function persistToServer(nextLocalView: Block[]) {
  try {
    setSaveState('saving')

    // Union: everything in cache + what the caller considers "next" for the visible range
    const union = dedupeBlocks([
      ...getAllBlocksFromCache(),  // previously fetched/edited weeks
      ...nextLocalView,            // the latest edits from the visible view
    ])

    await api.putStackAvailability(activeStack, {
      specialistId: me?.userId,
      blocks: union.map(b => ({ startISO: b.startISO, endISO: b.endISO })),
    })
    setNeedsProviderProfile(false)

    // Optional: keep cache in sync so a later navigation doesn’t “forget”
    // Write-through (treat all non-overlapping as additions)
    writeThroughCache(union, [])  // we’ve effectively replaced the world with union

    setSaveState('saved')
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setSaveState('idle'), 1200)
  } catch (e:any) {
    console.error('persistToServer failed', e)
    if (isForbiddenError(e)) {
      setNeedsProviderProfile(true)
      setSaveState('idle')
      setErr(null)
      return
    }
    setSaveState('error')
    setSaveMsg(e?.message || 'Failed to save')
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setSaveState('idle'), 3000)
  }
}

  // ---- Build calendar events ----
// ✅ Availability as background (clickable)
const fcAvailBgEvents = useMemo(
  () =>
    (serverBlocks || []).map(b => ({
      id: `${b.startISO}|${b.endISO}`,
      start: toDisplayISO(b.startISO, displayTz),
      end:   toDisplayISO(b.endISO,   displayTz),
      display: 'background',
      classNames: ['avail-bg'],
      extendedProps: {
        kind: 'avail',
        originalId: `${b.startISO}|${b.endISO}`,
      },
    })),
  [serverBlocks, displayTz]
)

const fcGcalBusyEvents = useMemo(
  () =>
    (gcalBusy || []).map(b => ({
      id: `gcal:${b.startISO}|${b.endISO}`,
      start: toDisplayISO(b.startISO, displayTz),
      end:   toDisplayISO(b.endISO,   displayTz),
      display: 'background',
      classNames: ['gcal-busy'],
      extendedProps: {
        kind: 'gcal',
      },
    })),
  [gcalBusy, displayTz]
)

const fcCalendlyBusyEvents = useMemo(
  () =>
    (calBusy || []).map(b => ({
      id: `cal:${b.startISO}|${b.endISO}`,
      start: toDisplayISO(b.startISO, displayTz),
      end:   toDisplayISO(b.endISO,   displayTz),
      display: 'background',
      classNames: ['calendly-busy'],
      extendedProps: {
        kind: 'calendly',
      },
    })),
  [calBusy, displayTz]
)

// ✅ Bookings (foreground, read-only)
const fcBookingEvents = useMemo(
  () =>
    (bookings || []).map(b => ({
      id: `bk:${b.startISO}`,
      start: toDisplayISO(b.startISO, displayTz),
      end:   toDisplayISO(b.endISO,   displayTz),
      title: b.title,
      editable: false,
      classNames: ['booking-event'],
      extendedProps: {
        kind: 'booking',
        appt: b.appt, // pass through full appt for the detail sheet
      },
    })),
  [bookings, displayTz]
)

const fcEvents = useMemo(
  () => [...fcAvailBgEvents, ...fcGcalBusyEvents, ...fcCalendlyBusyEvents, ...fcBookingEvents],
  [fcAvailBgEvents, fcGcalBusyEvents, fcCalendlyBusyEvents, fcBookingEvents]
)

  // ------- FullCalendar handlers (v5) -------
const onSelect = (sel: any) => {
  // If we're in month view, don't open the create modal
  if (calRef.current?.getApi()?.view.type === 'dayGridMonth') return

  // Open modal to choose duration + recurrence
  setFormDurMin(30)
  setFormRepeat('none')
  setFormCount(1)
  setFormUntil('')
  setModal({
    mode: 'create',
    startISO: DateTime.fromJSDate(sel.start, { zone: displayTz }).toISO()!,
    endISO:   DateTime.fromJSDate(sel.end,   { zone: displayTz }).toISO()!,
  })
}

const onEventClick = (arg: any) => {
  const id = String(arg?.event?.id || '')
  if (arg?.event?.extendedProps?.kind === 'gcal' || arg?.event?.extendedProps?.kind === 'calendly') return
  if (id.startsWith('bk:')) {
    // Open appointment detail sheet; keep the calendar in the background
    const appt = arg?.event?.extendedProps?.appt as Appt | undefined
    if (appt) {
      setCancelError(null)
      setActiveAppt(appt)
    }
    return
  }

  // resolve original availability id if coming from the hitbox
  const originalId = arg?.event?.extendedProps?.originalId || id

  // Edit/delete modal (availability)
  setFormDurMin(
    Math.max(5, Math.round((new Date(arg.event.end!).getTime() - new Date(arg.event.start!).getTime())/60000))
  )
  setFormRepeat('none')
  setFormCount(1)
  setFormUntil('')
  setModal({
    mode: 'edit',
    startISO: DateTime.fromJSDate(arg.event.start!, { zone: displayTz }).toISO()!,
    endISO:   DateTime.fromJSDate(arg.event.end!,   { zone: displayTz }).toISO()!,
    originalId
  })
}

const onEventChange = (chg: any) => {
  const { event } = chg
  if (String(event.id).startsWith('bk:')) return

  const oldId = event.extendedProps?.originalId || event.id

  const startCanon = DateTime.fromJSDate(event.start!, { zone: displayTz }).setZone(CANON_TZ).toUTC().toISO()
  const endCanon   = DateTime.fromJSDate(event.end!,   { zone: displayTz }).setZone(CANON_TZ).toUTC().toISO()

  const prev = serverBlocks
  const next = dedupeBlocks(
    prev.filter(b => blockId(b) !== oldId)
        .concat([{ startISO: startCanon!, endISO: endCanon! }])
  )

  setServerBlocks(next)
  const { added, removed } = diffBlocks(prev, next)
  writeThroughCache(added, removed)
  persistToServer(next)
}

const onDateClick = (info: any) => {
  if (info.view?.type === 'dayGridMonth') {
    calRef.current?.getApi()?.changeView('timeGridDay', info.date)
    return
  }
  // Create a default 30-minute block on tap for timeGrid views
  if (info.view?.type?.startsWith('timeGrid')) {
    const start = DateTime.fromJSDate(info.date, { zone: displayTz })
    const end = start.plus({ minutes: 30 })
    const newBlock: Block = {
      startISO: start.setZone(CANON_TZ).toUTC().toISO(),
      endISO: end.setZone(CANON_TZ).toUTC().toISO(),
    }
    const next = dedupeBlocks([...(serverBlocks || []), newBlock])
    setServerBlocks(next)
    const { added, removed } = diffBlocks(serverBlocks || [], next)
    writeThroughCache(added, removed)
    persistToServer(next)
  }
}

  const onDatesSet = (arg: any) => {
    if (needsProviderProfile) return
    // fetch a small buffer around the view (you already do this inside loadRange)
    const start = new Date(arg.start); start.setDate(start.getDate() - 2)
    const end   = new Date(arg.end);   end.setDate(end.getDate() + 2)

    // Always capture the visible range so Google busy can load as soon as status arrives.
    gcalRangeRef.current = { start, end, stack: activeStack }
    calRangeRef.current = { start, end, stack: activeStack }

    // fetch if needed
    loadRange(start, end)

    // IMPORTANT: merge what we already have in state (local edits)
    // with what's in the cache for the visible range, then set.
    const fromCache = mergeBlocksFromCacheVisible(start, end)
    const merged = dedupeBlocks([...(serverBlocks || []), ...fromCache])
    setServerBlocks(merged)
  }

  // local helpers
  function dedupeBlocks(arr: Block[]): Block[] {
    const map = new Map<string, Block>()
    for (const b of arr) map.set(`${b.startISO}|${b.endISO}`, b)
    return Array.from(map.values()).sort((a,b)=> a.startISO.localeCompare(b.startISO))
  }

  // -------- modal actions --------
  function closeModal() { setModal(null) }

function buildRecurrences(firstStartISO: string, durationMin: number): Block[] {
  const first = DateTime.fromISO(firstStartISO, { zone: displayTz })

  const makeBlock = (dt: DateTime): Block => ({
    startISO: dt.setZone(CANON_TZ).toUTC().toISO()!,
    endISO:   dt.plus({ minutes: durationMin }).setZone(CANON_TZ).toUTC().toISO()!,
  })

  if (formRepeat === 'none') return [makeBlock(first)]

  // If an "Until" date is set, we'll generate repeats up to that date (end of day).
  const until =
    formUntil && /^\d{4}-\d{2}-\d{2}$/.test(formUntil)
      ? DateTime.fromISO(formUntil, { zone: displayTz }).endOf('day')
      : null

  // If no "Until", use Occurrences; if both are set, we stop at whichever condition comes first.
  const maxCount = clamp(Number(formCount || 1), 1, 180)
  const blocks: Block[] = []

  let dt = first
  let i = 0
  while (true) {
    // stop rules
    if (!until && i >= maxCount) break
    if (until && dt > until) break

    blocks.push(makeBlock(dt))
    i++

    // advance dt based on repeat pattern
    if (formRepeat === 'daily')      dt = dt.plus({ days: 1 })
    else if (formRepeat === 'weekly')  dt = dt.plus({ weeks: 1 })
    else if (formRepeat === 'monthly') dt = dt.plus({ months: 1 })
    else break // safety
  }

  return blocks
}

function onCreateConfirm() {
  if (!modal) return
  const start = DateTime.fromISO(modal.startISO, { zone: displayTz })
  const end   = DateTime.fromISO(modal.endISO,   { zone: displayTz })
  const durMin = Math.max(1, Math.round(end.diff(start, 'minutes').minutes))

  const blocks = buildRecurrences(start.toISO()!, durMin)

  const prev = serverBlocks
  const next = dedupeBlocks([...prev, ...blocks])

  setServerBlocks(next)
  const { added, removed } = diffBlocks(prev, next)
  writeThroughCache(added, removed)

  persistToServer(next)
  closeModal()
}

function onEditSave() {
  if (!modal) return
  const startLocal = DateTime.fromISO(modal.startISO, { zone: displayTz })
  const endLocal   = DateTime.fromISO(modal.endISO,   { zone: displayTz })
  const durMin = Math.max(1, Math.round(endLocal.diff(startLocal, 'minutes').minutes))

  const newBlock: Block = {
    startISO: startLocal.setZone(CANON_TZ).toUTC().toISO()!,
    endISO:   startLocal.plus({ minutes: durMin }).setZone(CANON_TZ).toUTC().toISO()!
  }

  const prev = serverBlocks
  const next = dedupeBlocks(
    prev
      .filter(b => blockId(b) !== modal.originalId)
      .concat([newBlock])
  )

  setServerBlocks(next)
  const { added, removed } = diffBlocks(prev, next)
  writeThroughCache(added, removed)

  persistToServer(next)
  closeModal()
}

  function localHHMM(iso: string, tz: string) {
  return DateTime.fromISO(iso).setZone(tz).toFormat('HH:mm')
}

function onEditDelete() {
  if (!modal) return
  const prev = serverBlocks
  const next = prev.filter(b => `${b.startISO}|${b.endISO}` !== modal.originalId)
  setServerBlocks(next)
  const { added, removed } = diffBlocks(prev, next)
  writeThroughCache(added, removed)
  persistToServer(next)
  closeModal()
}

function onEditDeleteOne() {
  if (!modal?.originalId) return
  const prev = serverBlocks
  const next = prev.filter(b => `${b.startISO}|${b.endISO}` !== modal.originalId)
  setServerBlocks(next)
  const { added, removed } = diffBlocks(prev, next)
  writeThroughCache(added, removed)
  persistToServer(next)
  closeModal()
}

function onEditDeleteAll() {
  if (!modal) return

  const startLocal = DateTime.fromISO(modal.startISO, { zone: displayTz })
  const endLocal   = DateTime.fromISO(modal.endISO,   { zone: displayTz })
  const durMin = Math.max(1, Math.round(endLocal.diff(startLocal, 'minutes').minutes))
  const hhmm   = localHHMM(modal.startISO, displayTz)

  const prev = serverBlocks
  const next = prev.filter(b => {
    const bStartLocal = DateTime.fromISO(b.startISO).setZone(displayTz)
    const bEndLocal   = DateTime.fromISO(b.endISO).setZone(displayTz)
    const bDur = Math.max(1, Math.round(bEndLocal.diff(bStartLocal, 'minutes').minutes))
    return !(localHHMM(b.startISO, displayTz) === hhmm && bDur === durMin)
  })

  setServerBlocks(next)
  const { added, removed } = diffBlocks(prev, next)
  writeThroughCache(added, removed)
  persistToServer(next)
  closeModal()
}

function onEditDeleteFuture() {
  if (!modal) return

  const startLocal = DateTime.fromISO(modal.startISO, { zone: displayTz })
  const endLocal   = DateTime.fromISO(modal.endISO,   { zone: displayTz })
  const durMin = Math.max(1, Math.round(endLocal.diff(startLocal, 'minutes').minutes))
  const hhmm   = localHHMM(modal.startISO, displayTz)
  const thisStartUTC = DateTime.fromISO(modal.startISO, { zone: displayTz }).toUTC()

  const prev = serverBlocks
  const next = prev.filter(b => {
    const bStartLocal = DateTime.fromISO(b.startISO).setZone(displayTz)
    const bEndLocal   = DateTime.fromISO(b.endISO).setZone(displayTz)
    const bDur  = Math.max(1, Math.round(bEndLocal.diff(bStartLocal, 'minutes').minutes))
    const isSameSeries = (localHHMM(b.startISO, displayTz) === hhmm && bDur === durMin)
    const isFutureOrEqual = DateTime.fromISO(b.startISO).toUTC() >= thisStartUTC
    return !(isSameSeries && isFutureOrEqual)
  })

  setServerBlocks(next)
  const { added, removed } = diffBlocks(prev, next)
  writeThroughCache(added, removed)
  persistToServer(next)
  closeModal()
}

function onEventDidMount(arg: any) {
  // Only for our availability background blocks
  if (arg.event.extendedProps?.kind !== 'avail') return;

  const el = arg.el as HTMLElement;

  // Ensure the bg span can receive the pointer (belt & braces with CSS)
  el.style.pointerEvents = 'auto';
  el.style.cursor = 'pointer';

  // --- IMPORTANT: block the selection plugin from seeing this interaction ---
  // Do it in the *capture* phase as well, so it wins over FullCalendar’s listeners.
  const stopAll = (e: Event) => { e.stopPropagation(); };
  el.addEventListener('mousedown', stopAll, true);
  el.addEventListener('touchstart', stopAll, true);

  const handler = (e?: MouseEvent | TouchEvent) => {
    // Also stop on the click itself, and prevent default to be extra safe
    if (e) { e.stopPropagation(); e.preventDefault(); }

    setFormRepeat('none');
    setFormCount(1);
    setFormUntil('');
    setFormDurMin(
      Math.max(5, Math.round((arg.event.end!.getTime() - arg.event.start!.getTime()) / 60000))
    );
    setModal({
      mode: 'edit',
      startISO: DateTime.fromJSDate(arg.event.start!, { zone: displayTz }).toISO()!,
      endISO:   DateTime.fromJSDate(arg.event.end!,   { zone: displayTz }).toISO()!,
      originalId: arg.event.extendedProps?.originalId || arg.event.id,
    });
  };

  // Avoid duplicate listeners across mounts (e.g., React StrictMode)
  el.removeEventListener('click', (el as any).__availClickHandler);
  el.addEventListener('click', handler);
  (el as any).__availClickHandler = handler;
}

  const handleStackSwitch = (stackId: ProviderProfession) => {
    if (stackId === activeStack) return
    setActiveStack(stackId)
  }
  const toggleGcalCalendar = (id: string) => {
    setGcalSelected((prev) => {
      const has = prev.includes(id)
      const next = has ? prev.filter((item) => item !== id) : [...prev, id]
      const nextWrite = next.includes(gcalWriteId || '') ? gcalWriteId : (next[0] || null)
      setGcalWriteId(nextWrite)
      return next
    })
  }
  const toggleCalendlyEventType = (uri: string) => {
    setCalSelected((prev) => {
      const has = prev.includes(uri)
      return has ? prev.filter((item) => item !== uri) : [...prev, uri]
    })
  }
  const openGcalManage = async () => {
    setGcalManageOpen(true)
    setGcalError(null)
    setGcalLoading(true)
    try {
      const res = await api.googleCalendarCalendars(activeStack)
      setGcalCalendars(Array.isArray(res?.calendars) ? res.calendars : [])
      setGcalSelected(Array.isArray(res?.selectedCalendarIds) ? res.selectedCalendarIds : [])
      setGcalWriteId(res?.writeCalendarId || null)
    } catch (e: any) {
      setGcalError(e?.message || 'Failed to load Google Calendar')
    } finally {
      setGcalLoading(false)
    }
  }
  const openCalendlyManage = async () => {
    setCalManageOpen(true)
    setCalError(null)
    setCalLoading(true)
    try {
      const res = await api.calendlyEventTypes(activeStack)
      const types = Array.isArray(res?.eventTypes) ? res.eventTypes : []
      setCalEventTypes(types)
      setCalSelected(Array.isArray(res?.selectedEventTypeUris) ? res.selectedEventTypeUris : [])
    } catch (e: any) {
      setCalError(e?.message || 'Failed to load Calendly')
    } finally {
      setCalLoading(false)
    }
  }
  const connectGoogleCalendar = async () => {
    setGcalError(null)
    setGcalLoading(true)
    try {
      const returnTo = `${location.pathname}${location.search}`
      const res = await api.googleCalendarStart(activeStack, returnTo)
      if (res?.url) {
        window.location.href = res.url
        return
      }
      setGcalError('Google Calendar connect failed')
    } catch (e: any) {
      setGcalError(e?.message || 'Google Calendar connect failed')
    } finally {
      setGcalLoading(false)
    }
  }
  const connectCalendly = async () => {
    setCalError(null)
    setCalLoading(true)
    try {
      const returnTo = `${location.pathname}${location.search}`
      const res = await api.calendlyStart(activeStack, returnTo)
      if (res?.url) {
        window.location.href = res.url
        return
      }
      setCalError('Calendly connect failed')
    } catch (e: any) {
      setCalError(e?.message || 'Calendly connect failed')
    } finally {
      setCalLoading(false)
    }
  }
  const saveGcalSettings = async () => {
    setGcalSaving(true)
    setGcalError(null)
    try {
      const res = await api.googleCalendarSettings(activeStack, {
        selectedCalendarIds: gcalSelected,
        writeCalendarId: gcalWriteId || null,
      })
      const nextSelected = res?.calendar?.selectedCalendarIds || gcalSelected
      const nextWrite = res?.calendar?.writeCalendarId || gcalWriteId || null
      setGcalStatus((prev: any) => ({
        ...(prev || {}),
        connected: true,
        selectedCalendarIds: nextSelected,
        writeCalendarId: nextWrite,
      }))
      setGcalSelected(nextSelected)
      setGcalWriteId(nextWrite)
      setGcalNotice('Google Calendar settings saved.')
      setGcalManageOpen(false)
    } catch (e: any) {
      setGcalError(e?.message || 'Could not save Google Calendar settings')
    } finally {
      setGcalSaving(false)
    }
  }
  const saveCalendlySettings = async () => {
    setCalSaving(true)
    setCalError(null)
    try {
      const res = await api.calendlySettings(activeStack, {
        selectedEventTypeUris: calSelected,
      })
      const nextSelected = Array.isArray(res?.calendly?.selectedEventTypeUris)
        ? res.calendly.selectedEventTypeUris
        : calSelected
      setCalStatus((prev: any) => ({
        ...(prev || {}),
        connected: true,
        selectedEventTypeUris: nextSelected,
      }))
      setCalSelected(nextSelected)
      setCalNotice('Calendly settings saved.')
      setCalManageOpen(false)
      const current = calRangeRef.current
      if (current) {
        loadCalendlyBusy(current.start, current.end, activeStack)
      }
    } catch (e: any) {
      setCalError(e?.message || 'Could not save Calendly settings')
    } finally {
      setCalSaving(false)
    }
  }
  const disconnectGoogleCalendar = async () => {
    setGcalSaving(true)
    setGcalError(null)
    try {
      await api.googleCalendarDisconnect(activeStack)
      setGcalStatus({ connected: false })
      setGcalSelected([])
      setGcalWriteId(null)
      setGcalBusy([])
      setGcalNotice('Google Calendar disconnected.')
      setGcalManageOpen(false)
    } catch (e: any) {
      setGcalError(e?.message || 'Could not disconnect Google Calendar')
    } finally {
      setGcalSaving(false)
    }
  }
  const disconnectCalendly = async () => {
    setCalSaving(true)
    setCalError(null)
    try {
      await api.calendlyDisconnect(activeStack)
      setCalStatus({ connected: false })
      setCalSelected([])
      setCalEventTypes([])
      setCalBusy([])
      setCalNotice('Calendly disconnected.')
      setCalManageOpen(false)
    } catch (e: any) {
      setCalError(e?.message || 'Could not disconnect Calendly')
    } finally {
      setCalSaving(false)
    }
  }
  const renderStackSwitcher = () => {
    if (availableStacks.length <= 1) return null
    return (
      <div className="chip-row" role="tablist" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
        {availableStacks.map(stack => (
          <button
            key={stack.id}
            type="button"
            className={`chip ${stack.id === activeStack ? 'active' : ''}`}
            onClick={() => handleStackSwitch(stack.id)}
          >
            {stack.label}
          </button>
        ))}
      </div>
    )
  }
  if (!viewReady) {
    return <div className="container"><Spinner label="Loading availability…" /></div>
  }

  if (needsProviderProfile) {
    return (
      <div className="container page-wide availability-page">
        <div className="page-head">
          <div>
            <p className="hero-kicker">Professionals</p>
            <h1>Calendar</h1>
            <p className="hint">Manage availability and view confirmed bookings in one place.</p>
          </div>
        </div>
        {renderStackSwitcher()}
        <section className="card section-card">
          <h3>Complete your professional registration</h3>
          <p className="hint">Finish your registration to unlock availability and bookings.</p>
          <Link className="btn btn-primary" to="/professionals" style={{ marginTop: '.75rem' }}>
            Complete registration
          </Link>
        </section>
      </div>
    )
  }

  const closeActiveAppt = () => {
    setActiveAppt(null)
    setCancelError(null)
  }
  const cancelActiveBooking = async () => {
    if (!activeAppt?.selectionId) {
      setCancelError('This booking cannot be cancelled here.')
      return
    }
    if (!window.confirm('Cancel this booking? The client will be notified.')) return
    setCancelingBooking(true)
    setCancelError(null)
    try {
      await api.cancelSelection(activeStack, activeAppt.selectionId)
      setBookings((prev) => prev.filter((b) => b.appt?.selectionId !== activeAppt.selectionId))
      if (activeAppt.chosenSlotISO) {
        const cancelStart = new Date(activeAppt.chosenSlotISO).getTime()
        const cancelEnd = new Date(addMinutes(activeAppt.chosenSlotISO, DEFAULT_SLOT_MIN)).getTime()
        setGcalBusy((prev) =>
          prev.filter((b) => {
            const bs = new Date(b.startISO).getTime()
            const be = new Date(b.endISO).getTime()
            return !(bs === cancelStart && be === cancelEnd)
          })
        )
      }
      setActiveAppt(null)
      setCancelError(null)
      setGcalNotice('Booking cancelled.')
      const view = calRef.current?.getApi?.()?.view
      if (view?.activeStart && view?.activeEnd) {
        const start = new Date(view.activeStart); start.setDate(start.getDate() - 2)
        const end = new Date(view.activeEnd); end.setDate(end.getDate() + 2)
        fetched.current.delete(rangeKey(start, end))
        loadRange(start, end, activeStack)
        loadGoogleBusy(start, end, activeStack)
        loadCalendlyBusy(start, end, activeStack)
      }
    } catch (e: any) {
      setCancelError(e?.message || 'Failed to cancel booking')
    } finally {
      setCancelingBooking(false)
    }
  }

  const addBookingOverlay = (
    <AddBookingSheet
      open={addBookingOpen}
      stack={activeStack}
      onClose={() => setAddBookingOpen(false)}
      onCreated={() => {
        const current = gcalRangeRef.current
        if (current) {
          loadRange(current.start, current.end, activeStack)
          return
        }
        const view = calRef.current?.getApi?.()?.view
        if (view?.activeStart && view?.activeEnd) {
          const start = new Date(view.activeStart); start.setDate(start.getDate() - 2)
          const end = new Date(view.activeEnd); end.setDate(end.getDate() + 2)
          loadRange(start, end, activeStack)
        }
      }}
    />
  )

  return (
    <div className="container page-wide availability-page">
      <div className="page-head">
        <div>
          <p className="hero-kicker">Professionals</p>
          <h1>Calendar</h1>
        </div>
        <div className="inline-gap-2">
          <button className="btn btn-calendar" type="button" onClick={() => setAddBookingOpen(true)}>
            <CalendarPlus size={18} strokeWidth={1.6} aria-hidden="true" />
            Add Booking
          </button>
          <Link className="btn btn-calendar" to="/dashboard">
            <InboxIcon size={18} strokeWidth={1.6} aria-hidden="true" />
            Go to Dashboard
          </Link>
        </div>
      </div>
      {renderStackSwitcher()}
      {addBookingOverlay}

      <section className="card section-card gcal-card">
        <div className="gcal-card-row">
          <div>
            <h3>Google Calendar Sync</h3>
            <p className="hint">Block busy times and auto-add bookings by syncing Google Calendar.</p>
            {gcalStatus?.connected && (
              <div className="hint">
                Connected as {gcalStatus.email || 'Google account'} ·{' '}
                {Array.isArray(gcalStatus.selectedCalendarIds) ? gcalStatus.selectedCalendarIds.length : 0} calendars blocking
              </div>
            )}
          </div>
          <div className="inline-gap-2">
            {gcalStatus?.connected ? (
              <button className="btn" onClick={openGcalManage} disabled={gcalLoading}>
                Manage
              </button>
            ) : (
              <button className="btn btn-primary" onClick={connectGoogleCalendar} disabled={gcalLoading}>
                Connect Google Calendar
              </button>
            )}
          </div>
        </div>
        {gcalStatus?.connected && gcalSelectedKey && (
          <div className="hint gcal-legend-row">
            <span className="gcal-legend" /> Busy from Google Calendar
          </div>
        )}
      {gcalError && <div className="banner error" role="alert">{gcalError}</div>}
      {gcalNotice && <div className="banner success" role="status">{gcalNotice}</div>}
    </section>

      <section className="card section-card calendly-card">
        <div className="gcal-card-row">
          <div>
            <h3>Calendly Sync</h3>
            <p className="hint">Import Calendly events to keep your EUCA Match availability up to date.</p>
            {calStatus?.connected && (
              <div className="hint">
                Connected as {calStatus.name || 'Calendly account'} ·{' '}
                {calStatus.selectionMode === 'all'
                  ? 'All event types blocking'
                  : `${Array.isArray(calStatus.selectedEventTypeUris) ? calStatus.selectedEventTypeUris.length : 0} event types blocking`}
              </div>
            )}
          </div>
          <div className="inline-gap-2">
            {calStatus?.connected ? (
              <button className="btn" onClick={openCalendlyManage} disabled={calLoading}>
                Manage
              </button>
            ) : (
              <button className="btn btn-primary" onClick={connectCalendly} disabled={calLoading}>
                Connect Calendly
              </button>
            )}
          </div>
        </div>
        {calStatus?.connected && (
          <div className="hint gcal-legend-row">
            <span className="calendly-legend" /> Busy from Calendly
          </div>
        )}
        {calError && <div className="banner error" role="alert">{calError}</div>}
        {calNotice && <div className="banner success" role="status">{calNotice}</div>}
      </section>

      <div className="header">
        <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Display timezone:</span>
          <select
            className="input"
            value={displayTz}
            onChange={e => setDisplayTz(e.target.value)}
            style={{ maxWidth: 220 }}
          >
            {displayTzOptions.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
        <p className="hint availability-calendar-instructions" style={{ marginTop: 10 }}>
          Click the calendar to add a slot; click and pull to set a longer block.
        </p>
        <div aria-live="polite" className="hint">
          {saveState === 'saving' && 'Saving…'}
          {saveState === 'saved'  && 'Saved'}
          {saveState === 'error'  && <span style={{ color: 'var(--error)' }}>{saveMsg}</span>}
        </div>
      </div>

      {err && <div className="banner error" role="alert">{err}</div>}
      <section>
        <div className={showOvernight ? 'card availability-calendar is-overnight' : 'card availability-calendar'}>
    <FullCalendar
      ref={calRef}
      plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin, luxon2Plugin]}
      initialView="timeGridWeek"
      headerToolbar={{
        left: 'prev,next today',
        center: 'title',
        right: 'timeGridWeek,timeGridDay,dayGridMonth'
      }}
      slotMinTime="00:00:00"
      slotMaxTime="24:00:00"
      scrollTime="07:00:00"
      allDaySlot={false}
      height="auto"
      contentHeight="auto"
      selectLongPressDelay={0}
      selectMinDistance={0}
      selectable
      selectAllow={() => calRef.current?.getApi()?.view.type !== 'dayGridMonth'}
      selectMirror
      editable
      eventResizableFromStart
      timeZone={displayTz}
      events={fcEvents}
      eventDidMount={onEventDidMount}
      dateClick={onDateClick}
      select={onSelect}
      eventClick={onEventClick}
      eventChange={onEventChange}
      datesSet={onDatesSet}
      eventOverlap={true}
      eventOrder={'-allDay,-duration,start,-id'}
      nowIndicator
          />
          {/* Appointment Detail sheet (stays on calendar when closed) */}
          {activeAppt && (
            <div className="overlay show" role="dialog" aria-modal="true" onClick={closeActiveAppt}>
              <div className="sheet" onClick={e => e.stopPropagation()}>
                <div className="sheet-header">
                  <div className="sheet-title">
                    <span style={{ fontWeight: 700 }}>
                      {activeAppt.specialty || 'Consultation'}
                    </span>
                  </div>
                  <button className="btn close" onClick={closeActiveAppt} aria-label="Close">✕</button>
                </div>

                <div className="sheet-body">
                  <div className="list">
                    <div>
                      <b>{appointmentTimeLabel(activeAppt.chosenSlotISO)}:</b>{' '}
                      {activeAppt.chosenSlotISO === CONTACT_SLOT
                        ? `${fmtDateTimeLocal(activeAppt.createdAt, displayTz)} (${displayTz})`
                        : `${fmtDateTimeLocal(activeAppt.chosenSlotISO, displayTz)} (${displayTz})`}
                    </div>
                    <div>
                      <b>Client:</b>{' '}
                      {activeAppt.patientName || activeAppt.patient?.name || '—'}
                    </div>
                    <div><b>Fee:</b> {money(activeAppt.feeCents, activeStackCurrency)}</div>

                    {!!activeAppt.meta && (
                      <>
                        <hr />
                        <div className="minor">Reference</div>
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                          {JSON.stringify(activeAppt.meta, null, 2)}
                        </pre>
                      </>
                    )}
                  </div>
                </div>

                {cancelError && (
                  <div className="banner error" role="alert" style={{ margin: '0 24px 8px' }}>
                    {cancelError}
                  </div>
                )}

                <div className="sheet-footer">
                  {activeAppt.selectionId &&
                    !['CANCELLED', 'COMPLETED'].includes(String(activeAppt.status || '').toUpperCase()) && (
                    <button
                      className="btn btn-outline"
                      style={{ borderColor: 'var(--error)', color: 'var(--error)' }}
                      onClick={cancelActiveBooking}
                      disabled={cancelingBooking}
                    >
                      {cancelingBooking ? 'Cancelling…' : 'Cancel booking'}
                    </button>
                  )}
                  <a
                    className="btn"
                    href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(activeAppt.specialty || 'Consultation')}&dates=${toGCalTime(activeAppt.chosenSlotISO)}/${toGCalTime(addMinutes(activeAppt.chosenSlotISO, 30))}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open calendar
                  </a>
                  <button className="btn" onClick={() => setActiveAppt(null)}>Close</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {gcalManageOpen && (
        <div className="overlay show" role="dialog" aria-modal="true" onClick={() => setGcalManageOpen(false)}>
          <div
            className="sheet"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 720, margin: '6vh auto', borderRadius: 16 }}
          >
            <div className="sheet-header">
              <div className="sheet-title">
                <span style={{ fontWeight: 700 }}>Google Calendar</span>
                <span className="minor">EUCA Match sync settings</span>
              </div>
              <button className="btn close" onClick={() => setGcalManageOpen(false)} aria-label="Close">✕</button>
            </div>

            <div className="sheet-body">
              {gcalLoading ? (
                <div style={{ padding: '0.5rem 0' }}>
                  <Spinner label="Loading calendars…" />
                </div>
              ) : (
                <>
                  <div className="hint" style={{ marginBottom: 12 }}>
                    Select the calendars that should block availability in EUCA Match.
                  </div>
                  <div className="gcal-calendar-list">
                    {gcalCalendars.length ? (
                      gcalCalendars.map((cal) => (
                        <label key={cal.id} className="gcal-calendar-option">
                          <input
                            type="checkbox"
                            checked={gcalSelectedSet.has(cal.id)}
                            onChange={() => toggleGcalCalendar(cal.id)}
                          />
                          <span>
                            {cal.summary || cal.id}
                            {cal.primary ? ' (Primary)' : ''}
                          </span>
                        </label>
                      ))
                    ) : (
                      <div className="hint">No calendars found for this account.</div>
                    )}
                  </div>

                  <div className="gcal-write-row">
                    <label className="minor" htmlFor="gcal-write">
                      Write EUCA Match bookings to
                    </label>
                    <select
                      id="gcal-write"
                      className="input"
                      value={gcalWriteId || ''}
                      onChange={(e) => setGcalWriteId(e.target.value || null)}
                      disabled={!gcalWriteOptions.length}
                    >
                      {!gcalWriteOptions.length && <option value="">Select a calendar</option>}
                      {gcalWriteOptions.map((cal) => (
                        <option key={cal.id} value={cal.id}>
                          {cal.summary || cal.id}{cal.primary ? ' (Primary)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {gcalError && (
                    <div className="banner error" role="alert" style={{ marginTop: 12 }}>
                      {gcalError}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="sheet-footer">
              <button className="btn btn-outline" onClick={disconnectGoogleCalendar} disabled={gcalSaving}>
                Disconnect
              </button>
              <button className="btn" onClick={() => setGcalManageOpen(false)} disabled={gcalSaving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveGcalSettings} disabled={gcalSaving}>
                {gcalSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {calManageOpen && (
        <div className="overlay show" role="dialog" aria-modal="true" onClick={() => setCalManageOpen(false)}>
          <div
            className="sheet"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 720, margin: '6vh auto', borderRadius: 16 }}
          >
            <div className="sheet-header">
              <div className="sheet-title">
                <span style={{ fontWeight: 700 }}>Calendly</span>
                <span className="minor">EUCA Match sync settings</span>
              </div>
              <button className="btn close" onClick={() => setCalManageOpen(false)} aria-label="Close">✕</button>
            </div>

            <div className="sheet-body">
              {calLoading ? (
                <div style={{ padding: '0.5rem 0' }}>
                  <Spinner label="Loading event types…" />
                </div>
              ) : (
                <>
                  <div className="hint" style={{ marginBottom: 12 }}>
                    Select the Calendly event types that should block availability in EUCA Match.
                  </div>
                  <div className="gcal-calendar-list">
                    {calEventTypes.length ? (
                      calEventTypes.map((eventType) => (
                        <label key={eventType.uri} className="gcal-calendar-option">
                          <input
                            type="checkbox"
                            checked={calSelectedSet.has(eventType.uri)}
                            onChange={() => toggleCalendlyEventType(eventType.uri)}
                          />
                          <span>
                            {eventType.name || eventType.uri}
                          </span>
                        </label>
                      ))
                    ) : (
                      <div className="hint">No event types found for this account.</div>
                    )}
                  </div>

                  {calError && (
                    <div className="banner error" role="alert" style={{ marginTop: 12 }}>
                      {calError}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="sheet-footer">
              <button className="btn btn-outline" onClick={disconnectCalendly} disabled={calSaving}>
                Disconnect
              </button>
              <button className="btn" onClick={() => setCalManageOpen(false)} disabled={calSaving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveCalendlySettings} disabled={calSaving}>
                {calSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {saveState !== 'idle' && (
        <div aria-live="polite" aria-atomic="true" style={{ position:'fixed', left:'50%', bottom:16, transform:'translateX(-50%)', zIndex:9999 }}>
          <div className="card" style={{
            padding: '.55rem .8rem',
            minWidth: 220,
            borderLeft:
              saveState === 'saving' ? '6px solid #94a3b8' :
              saveState === 'saved'  ? '6px solid #22c55e' :
                                        '6px solid #ef4444',
          }}>
            {saveState === 'saving' && 'Saving…'}
            {saveState === 'saved'  && '✓ Saved'}
            {saveState === 'error'  && '⚠︎ Failed to save'}
          </div>
        </div>
      )}

      {/* Create/Edit modal */}
      {modal && (
        <div className="overlay show" onClick={closeModal}>
          <div
            className="sheet"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 520, margin: '6vh auto', borderRadius: 16 }}
          >
            <div className="sheet-header">
              <div className="sheet-title">
                {modal.mode === 'create' ? 'Add availability' : 'Edit availability'}
              </div>
              <button className="btn close" onClick={closeModal}>✕</button>
            </div>

            <div className="sheet-body">
              <div className="row">
                <div className="col">
                  <div className="field-label">Start (in {displayTz})</div>
                  <input
                    className="input"
                    type="datetime-local"
                    value={DateTime.fromISO(modal.startISO, { zone: displayTz }).toFormat("yyyy-LL-dd'T'HH:mm")}
                    onChange={e => {
                      const dt = DateTime.fromFormat(e.target.value, "yyyy-LL-dd'T'HH:mm", { zone: displayTz })
                      setModal(m => m ? { ...m, startISO: dt.toISO()! } : m)
                    }}
                  />
                </div>
                <div className="col">
                  <div className="field-label">End (in {displayTz})</div>
                  <input
                    className="input"
                    type="datetime-local"
                    value={DateTime.fromISO(modal.endISO, { zone: displayTz }).toFormat("yyyy-LL-dd'T'HH:mm")}
                    onChange={e => {
                      const dt = DateTime.fromFormat(e.target.value, "yyyy-LL-dd'T'HH:mm", { zone: displayTz })
                      setModal(m => m ? { ...m, endISO: dt.toISO()! } : m)
                    }}
                  />
                </div>
              </div>

              {modal.mode === 'create' && (
                <>
                  <div className="row">
                    <div className="col">
                      <div className="field-label">Repeat</div>
                      <select className="input" value={formRepeat} onChange={e => setFormRepeat(e.target.value as any)}>
                        <option value="none">Does not repeat</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly (same weekday)</option>
                        <option value="monthly">Monthly (same date)</option>
                      </select>
                    </div>
                    <div className="col">
                      <div className="field-label">Occurrences</div>
                      <input
                        className="input"
                        type="number"
                        min={1}
                        max={180}
                        value={formCount}
                        onChange={e => setFormCount(Number(e.target.value || 1))}
                      />
                      <div className="hint">Or set “Until”</div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <div className="field-label">Until (optional)</div>
                      <input
                        className="input"
                        type="date"
                        value={formUntil}
                        onChange={e => setFormUntil(e.target.value)}
                      />
                      <div className="hint">If set, overrides Occurrences when earlier.</div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="sheet-footer">
              {modal.mode === 'edit' ? (
                <>
                  <div className="inline-gap-2">
                    <button className="btn" onClick={onEditDeleteOne}>Delete this</button>
                    <button className="btn" onClick={onEditDeleteFuture}>Delete future</button>
                    <button className="btn" onClick={onEditDeleteAll}>Delete all</button>
                  </div>
                  <div style={{ flex: 1 }} />
                  <button className="btn btn-primary" onClick={onEditSave}>Save</button>
                </>
              ) : (
                <>
                  <div style={{ flex: 1 }} />
                  <button className="btn btn-primary" onClick={onCreateConfirm}>Add</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
